'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styles from './NavLink.module.scss';
import { UrlObject } from 'url';
import { Route } from 'next';

type NavLinkProps = {
  href?: UrlObject | Route;
  target?: string;
  children: React.ReactNode;
  currentPathname?: string; // Optional: pass from parent to avoid hook when possible
};

export default function NavLink({
  href = '/en' as Route,
  children,
  target = '_self',
  currentPathname,
}: NavLinkProps) {
  const pathnameFromHook = usePathname();
  const pathname = currentPathname ?? pathnameFromHook;

  // If it's an object with hash (anchor link)
  if (
    typeof href === 'object' &&
    href !== null &&
    'hash' in href &&
    typeof href.hash === 'string' &&
    href.hash.length > 0
  ) {
    const hash = href.hash.startsWith('#') ? href.hash : `#${href.hash}`;
    const hrefPathname = 'pathname' in href ? href.pathname : undefined;

    // If pathname is provided and different from current, use Next.js Link
    if (hrefPathname && hrefPathname !== pathname) {
      return (
        <Link
          href={{ pathname: hrefPathname, hash } as UrlObject}
          target={target}
          className={styles.link}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {children}
        </Link>
      );
    }

    // Same page anchor link - use regular anchor tag
    return (
      <a href={hash} className={styles.link} target={target}>
        {children}
      </a>
    );
  }

  // String anchor link (starts with #)
  if (typeof href === 'string' && href.startsWith('#')) {
    return (
      <a href={href} className={styles.link} target={target}>
        {children}
      </a>
    );
  }

  // Use Next.js Link for everything else
  return (
    <Link
      href={href}
      target={target}
      className={styles.link}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {children}
    </Link>
  );
}
